import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  DotsHorizontal,
  Edit03,
  Menu,
  MenuItem,
  ModalPresetConfirm,
  Popper,
  useModal,
  useSearchFilter,
} from '@dashboard/ui'
import {
  DrawerSidebar,
  PageHeader,
  Pagination,
  Tab,
  Table,
  TableCell,
  TableCellBulkCheckbox,
  TableCellBulkToolbar,
  TableRow,
  TableRowSkeleton,
  TableZeroState,
  Tabs,
  usePagination,
} from '@/ui'
import { Trash } from '@/icons'
import { useCheckoutLinksQuery } from './api'
import { CheckoutLinkRead, ResourceState } from '@/api/dashboard'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useBulkSelectGeneric } from '@/libs/hooks/useBulkSelectGeneric'
import { dashboardClient } from '@/api'
import { SKU_CHECKOUT_LINKS_DETAILS_PATH, SKU_CHECKOUT_LINKS_PATH, useGenerateCompanyPath } from '@/libs'
import { URL_NEW } from '@/types'
import { TableZeroStateFilter } from '@/layouts/components/TableZeroStateFilter'
import { DEFAULT_LOAD_LIMIT } from '@/api/useGameItems'
import { SkuImageName } from '@/layouts/game-items'
import { CheckoutLinkSettings } from '@/layouts/checkout-links/CheckoutLinkSettings'
import { useCountLinksQuery } from '@/layouts/checkout-links/api/useCountLinksQuery'
import { CopyButton } from '@/components/ui/LabelCopyButton/CopyButton'
import { PriceView } from '@/layouts/game-items/components/PriceView'

type PromotionFilter = 'state'

enum TabState {
  all = 'all',
  active = 'active',
  archived = 'archived',
}

export const CheckoutLinksPage = () => {
  const { companyId, gameId, checkoutLinkId } = useParams() as {
    companyId: string
    gameId: string
    checkoutLinkId: string
  }
  const navigate = useNavigate()
  const { t } = useTranslation('checkout-links')
  const { t: tMain } = useTranslation()
  const { canEdit } = useCurrentUser()
  const { generatePath } = useGenerateCompanyPath()
  const filter = useSearchFilter<PromotionFilter>({
    state: undefined,
  })
  const { page, query, onPageChange, needShowPagination } = usePagination(DEFAULT_LOAD_LIMIT)

  const getResourceQueryParam = (): ResourceState | undefined => {
    switch (filter.values.state) {
      case TabState.all:
        return undefined
      case ResourceState.Archived:
        return ResourceState.Archived
      case TabState.active:
      default:
        return ResourceState.Active
    }
  }

  const {
    data: items = [],
    isLoading,
    refetch,
  } = useCheckoutLinksQuery(companyId, gameId, {
    ...query,
    resource_state: getResourceQueryParam(),
  })

  const { data: countCheckoutLinks, refetch: refetchCount } = useCountLinksQuery(companyId, gameId)

  const { selected, onSelect, onReset } = useBulkSelectGeneric()

  const openBulkArchiveModal = useModal(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        subtitle: t(filter.values.state == ResourceState.Archived ? 'unarchive-n-items' : 'archive-n-items', {
          N: selected.length,
        }),
        children: tMain('confirm.title'),
      }}
      cancel={{
        label: tMain('Cancel'),
      }}
      submit={{
        color: 'danger',
        callback: () => {
          props.onClose()
          onArchivedChange(items.filter(it => selected.includes(it.id)))
        },
        label: tMain('archive'),
      }}
    />
  ))

  const onArchivedChange = async (item: CheckoutLinkRead[]) => {
    const data = item.map(it => {
      const res = {
        archived_at: (it.archived_at ? null : new Date().getTime() / 1000) as number,
        active: undefined as boolean | undefined,
        id: it.id,
      }

      if (res.archived_at) {
        res.active = false
      }

      return res
    })

    await dashboardClient.v1.bulkUpdateCheckoutLinks(companyId, gameId, data)
    refetchCount()
    await refetch()

    onReset([])
  }

  const openConfirmArchive = useModal<{ item: CheckoutLinkRead }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: tMain('confirm.title'),
        subtitle: t(props.item.archived_at ? 'unarchive' : 'archive', {
          name: props.item.items[0].name,
        }),
      }}
      cancel={{
        label: tMain('no'),
      }}
      submit={{
        color: props.item.archived_at ? 'primary' : 'danger',
        callback: () => {
          props.onClose()
          onArchivedChange([props.item])
        },
        label: tMain(props.item.archived_at ? 'unarchive' : 'archive'),
      }}
    />
  ))

  const openPromotionSettings = (id: string) => {
    navigate(generatePath(SKU_CHECKOUT_LINKS_DETAILS_PATH, { checkoutLinkId: id }, true))
  }

  const renderDefaultHeader = () => {
    return (
      <TableRow variant="header">
        {items.length > 0 && canEdit && filter.values.state != 'all' && (
          <TableCellBulkCheckbox
            selected={selected.length > 0 && selected.length == items.length}
            disabled={items.length === 0}
            onChange={() => onReset(selected.length == items.length ? [] : items.map(i => i.id))}
          />
        )}
        <TableCell width="50%">{t('table.name')}</TableCell>
        <TableCell width="70%">{t('table.link')}</TableCell>
        <TableCell width="10%">{tMain('sku.item.price')}</TableCell>
        <TableCell width={80} />
      </TableRow>
    )
  }

  const renderBulkSelectionHeader = () => (
    <TableRow variant="header">
      <TableCellBulkCheckbox
        selected={selected.length > 0 && selected.length == items.length}
        disabled={items.length === 0}
        onChange={() => onReset(selected.length == items.length ? [] : items.map(i => i.id))}
      />
      <TableCellBulkToolbar selected={selected} onReset={() => onReset([])}>
        <Button variant="tertiary" color="danger" size="sm" onClick={openBulkArchiveModal}>
          <Trash size={16} />
          <span>{filter.values.state == ResourceState.Archived ? tMain('unarchive') : tMain('archive')}</span>
        </Button>
      </TableCellBulkToolbar>
    </TableRow>
  )

  const renderZeroState = () => {
    if (filter.values.state == ResourceState.Archived) {
      return <TableZeroState title={t('zero-archived')} message={t('archived.message')} />
    }

    return (
      <TableZeroStateFilter
        page={page}
        items={items}
        hasFilter={false}
        createClick={() => {
          openPromotionSettings(URL_NEW)
        }}
        zeroStateMessage={t('zero.title')}
        zeroStateSubMessage={t('zero.message')}
        addButtonText={t('add')}
        clearFilterClick={() => {
          filter.onChange({
            state: filter.values.state,
          })
        }}
      />
    )
  }

  const makeUrl = (link: CheckoutLinkRead) => {
    return link.url
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        extra={
          canEdit && (
            <>
              {!!items.length && (
                <Button size="sm" onClick={() => openPromotionSettings(URL_NEW)}>
                  {t('add')}
                </Button>
              )}
            </>
          )
        }
      >
        {t('title')}
      </PageHeader>

      <Tabs className="mb-4">
        <Tab
          isActive={filter.values.state == TabState.all}
          onClick={() => {
            filter.onChange({ ...filter.values, state: TabState.all }, { force: true })
          }}
          count={countCheckoutLinks?.all_count}
        >
          {t('tab.all')}
        </Tab>
        <Tab
          isActive={!filter.values.state}
          onClick={() => {
            filter.onChange({ ...filter.values, state: undefined }, { force: true })
          }}
          count={countCheckoutLinks?.active_count}
        >
          {t('tab.active')}
        </Tab>
        <Tab
          isActive={filter.values.state == TabState.archived}
          onClick={() => {
            filter.onChange({ ...filter.values, state: TabState.archived }, { force: true })
          }}
          count={countCheckoutLinks?.archived_count}
        >
          {t('tab.archived')}
        </Tab>
      </Tabs>

      {canEdit && (
        <DrawerSidebar open={!!checkoutLinkId} width="720px" position="right">
          {checkoutLinkId && (
            <CheckoutLinkSettings
              onClose={() => {
                navigate(generatePath(SKU_CHECKOUT_LINKS_PATH, undefined, true))
              }}
            />
          )}
        </DrawerSidebar>
      )}

      {isLoading ? (
        <TableRowSkeleton rowCount={query.limit} columnCount={3} />
      ) : items.length === 0 && page == 1 ? (
        renderZeroState()
      ) : (
        <>
          <Table>
            {selected.length == 0 ? renderDefaultHeader() : renderBulkSelectionHeader()}
            {items.map(it => (
              <TableRow key={it.id} onClick={() => openPromotionSettings(it.id)} className="cursor-pointer">
                {canEdit && filter.values.state != 'all' && (
                  <TableCellBulkCheckbox selected={selected.includes(it.id)} onChange={() => onSelect(it.id)} />
                )}
                <TableCell width="50%">
                  <SkuImageName item={it.items[0]} />
                </TableCell>

                <TableCell width="70%" className="flex items-center gap-2 whitespace-nowrap">
                  <CopyButton text={makeUrl(it)} />
                  <a href={makeUrl(it)} target="_blank" onClick={e => e.stopPropagation()}>
                    {makeUrl(it)}
                  </a>
                </TableCell>
                <TableCell width="10%" className="whitespace-nowrap">
                  <PriceView item={it.items[0]} />
                </TableCell>

                <TableCell width={80}>
                  {canEdit && (
                    <div
                      className="text-right"
                      onClick={e => {
                        e.stopPropagation()
                        e.preventDefault()
                      }}
                    >
                      <Popper
                        popper={props => (
                          <Menu {...props}>
                            <MenuItem
                              icon={Edit03}
                              onClick={() => {
                                openPromotionSettings(it.id)
                                props.onClose()
                              }}
                            >
                              {tMain('edit')}
                            </MenuItem>
                            {!it.archived_at && (
                              <MenuItem
                                icon={Trash}
                                variant="danger"
                                onClick={() => {
                                  openConfirmArchive({
                                    item: it,
                                  })
                                  props.onClose()
                                }}
                              >
                                {tMain('archive')}
                              </MenuItem>
                            )}
                            {!!it.archived_at && (
                              <MenuItem
                                icon={Trash}
                                variant="danger"
                                onClick={() => {
                                  openConfirmArchive({
                                    item: it,
                                  })
                                  props.onClose()
                                }}
                              >
                                {tMain('unarchive')}
                              </MenuItem>
                            )}
                          </Menu>
                        )}
                      >
                        <Button variant="tertiary" color="secondary">
                          <DotsHorizontal />
                        </Button>
                      </Popper>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </Table>
          {needShowPagination(isLoading, items) && (
            <Pagination
              hasNext={items.length >= query.limit}
              page={page}
              onPageChange={onPageChange}
              pageItems={items.length}
            />
          )}
        </>
      )}
    </div>
  )
}
